// Import/Export Wizard API using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getAccessToken } from './auth-token'
import { getSchoolContext } from './school-context'
import type {
  ImportSession,
  ImportResult,
  ExportData,
  CreateSessionRequest,
  UpdateSessionRequest,
  ParsedMasterData,
  ParsedTeacher,
  ParsedSubject,
  ParsedClass,
  ParsedRoom,
  ParsedPeriod,
  ParsedTeacherSubject,
  ParsedTeacherClass,
  ParsedSubjectClass,
  ParsedClassScheduleAssignment,
  TimetableRow,
} from '@timetable/types'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

// ============================================================================
// Helpers
// ============================================================================

/** Fetch all teacher types (global table, no school_id). Creates defaults if none exist. */
async function fetchAllTeacherTypes(
  accessToken: string
): Promise<{ id: string; type_name: string }[]> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/teacher_types?select=id,type_name`,
    {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(`Failed to fetch teacher types: ${err?.message ?? response.statusText}`)
  }
  const data: { id: string; type_name: string }[] = await response.json()
  if (data.length > 0) return data

  // No teacher types exist — try to create defaults
  const defaults = ['Permanent', 'Contract', 'Guest', 'Part-Time', 'General']
  for (const type_name of defaults) {
    const createResp = await fetch(`${SUPABASE_URL}/rest/v1/teacher_types`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify({ type_name }),
    })
    if (createResp.ok) {
      const created: { id: string; type_name: string }[] = await createResp.json()
      if (created.length > 0) {
        // Re-fetch all after creating
        const refetch = await fetch(`${SUPABASE_URL}/rest/v1/teacher_types?select=id,type_name`, {
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
        })
        if (refetch.ok) return refetch.json()
      }
    }
  }

  throw new Error(
    'No teacher types found in the system. Please add teacher types in Settings → Teacher Types before importing.'
  )
}

/** Resolve a teacher_type UUID by type_name (case-insensitive). Falls back to first available type. */
function resolveTeacherTypeId(
  typeName: string,
  allTypes: { id: string; type_name: string }[]
): string | null {
  if (allTypes.length === 0) return null
  const lower = typeName?.toLowerCase().trim() ?? ''
  const match = allTypes.find((t) => t.type_name.toLowerCase() === lower)
  return match?.id ?? allTypes[0].id
}

// ============================================================================
// Session CRUD — Requirements 10.2, 10.3
// ============================================================================

/**
 * Fetch the single `in_progress` import session for a school, or null if none.
 */
export async function getImportSessionDirect(schoolCode: string): Promise<ImportSession | null> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams({
      school_id: `eq.${schoolCode}`,
      status: 'eq.in_progress',
      order: 'created_at.desc',
      limit: '1',
    })

    const response = await fetch(`${SUPABASE_URL}/rest/v1/import_sessions?${params}`, {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch import session')
    }

    const data: ImportSession[] = await response.json()
    return data[0] ?? null
  })
}

/**
 * Create a new import session for a school.
 */
export async function createImportSessionDirect(req: CreateSessionRequest): Promise<ImportSession> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const body = {
      school_id: req.school_id,
      academic_year: req.academic_year,
      started_by: req.started_by,
      status: 'in_progress',
      current_step: 1,
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/import_sessions`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create import session')
    }

    const data: ImportSession[] = await response.json()
    return data[0]
  })
}

/**
 * Patch an existing import session with updated fields.
 */
export async function updateImportSessionDirect(
  sessionId: string,
  patch: UpdateSessionRequest
): Promise<ImportSession> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/import_sessions?id=eq.${sessionId}`,
      {
        method: 'PATCH',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          Prefer: 'return=representation',
        },
        body: JSON.stringify(patch),
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update import session')
    }

    const data: ImportSession[] = await response.json()
    if (!data[0]) throw new Error('Import session not found')
    return data[0]
  })
}

// ============================================================================
// Session Lock Functions — Requirements 1.7, 1.8, 10.6
// ============================================================================

const LOCK_EXPIRY_MINUTES = 15

/**
 * Acquire the session lock for a user.
 * Rejects if the lock is held by a different user and has not expired (< 15 min).
 */
export async function acquireSessionLockDirect(
  sessionId: string,
  userId: string
): Promise<ImportSession> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Fetch current session to check lock state
    const getResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/import_sessions?id=eq.${sessionId}&select=id,locked_by,locked_at,status`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/vnd.pgrst.object+json',
        },
      }
    )

    if (!getResponse.ok) throw new Error('Failed to fetch session for lock acquisition')
    const session: Pick<ImportSession, 'id' | 'locked_by' | 'locked_at' | 'status'> =
      await getResponse.json()

    // Check if lock is held by someone else and not expired
    if (session.locked_by && session.locked_by !== userId && session.locked_at) {
      const lockedAt = new Date(session.locked_at).getTime()
      const ageMinutes = (Date.now() - lockedAt) / 60_000
      if (ageMinutes < LOCK_EXPIRY_MINUTES) {
        throw new Error(
          `Session is locked by another user. Lock expires in ${Math.ceil(LOCK_EXPIRY_MINUTES - ageMinutes)} minute(s).`
        )
      }
    }

    // Acquire the lock
    const patchResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/import_sessions?id=eq.${sessionId}`,
      {
        method: 'PATCH',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          Prefer: 'return=representation',
        },
        body: JSON.stringify({ locked_by: userId, locked_at: new Date().toISOString() }),
      }
    )

    if (!patchResponse.ok) {
      const error = await patchResponse.json()
      throw new Error(error.message || 'Failed to acquire session lock')
    }

    const data: ImportSession[] = await patchResponse.json()
    if (!data[0]) throw new Error('Import session not found')
    return data[0]
  })
}

/**
 * Release the session lock held by a user.
 * No-op if the lock is held by a different user.
 */
export async function releaseSessionLockDirect(
  sessionId: string,
  userId: string
): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Only release if this user holds the lock
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/import_sessions?id=eq.${sessionId}&locked_by=eq.${userId}`,
      {
        method: 'PATCH',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ locked_by: null, locked_at: null }),
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to release session lock')
    }
  })
}

/**
 * Abandon a session: set status to 'abandoned' and clear the lock.
 */
export async function abandonSessionDirect(
  sessionId: string,
  userId: string
): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/import_sessions?id=eq.${sessionId}`,
      {
        method: 'PATCH',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'abandoned',
          locked_by: null,
          locked_at: null,
          updated_by: userId,
        }),
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to abandon session')
    }
  })
}

// ============================================================================
// Import Execution — Requirements 9.3, 9.4, 9.5, 9.6
// ============================================================================

interface UpsertCounts {
  inserted: number
  updated: number
}

/**
 * Upsert teachers and return inserted/updated counts.
 * Unique key: (school_id, name)
 */
async function upsertTeachers(
  accessToken: string,
  schoolCode: string,
  teachers: ParsedTeacher[]
): Promise<UpsertCounts> {
  if (teachers.length === 0) return { inserted: 0, updated: 0 }

  // Fetch existing teachers to determine inserted vs updated
  const existingResp = await fetch(
    `${SUPABASE_URL}/rest/v1/teachers?school_id=eq.${encodeURIComponent(schoolCode)}&select=name`,
    {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    }
  )
  const existing: { name: string }[] = existingResp.ok ? await existingResp.json() : []
  const existingNames = new Set(existing.map((t) => t.name))

  // Fetch all teacher types once (global table, no school_id)
  const allTypes = await fetchAllTeacherTypes(accessToken)

  // Resolve teacher_type_ids
  const allRows = await Promise.all(
    teachers.map(async (t) => {
      const typeId = resolveTeacherTypeId(t.teacher_type, allTypes)
      if (!typeId) throw new Error(`Could not resolve teacher type for "${t.teacher_type}"`)
      return {
        school_id: schoolCode,
        name: t.name,
        email: t.email ?? null,
        phone_number: t.phone ?? null,
        teacher_type_id: typeId,
      }
    })
  )

  // Deduplicate by name — same name twice in one batch causes ON CONFLICT error
  const seenNames = new Set<string>()
  const rows = allRows.filter((r) => {
    if (seenNames.has(r.name)) return false
    seenNames.add(r.name)
    return true
  })

  const response = await fetch(`${SUPABASE_URL}/rest/v1/teachers?on_conflict=school_id,name`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates,return=minimal',
    },
    body: JSON.stringify(rows),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(`[teachers] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
  }

  const inserted = rows.filter((r) => !existingNames.has(r.name)).length
  const updated = rows.length - inserted
  return { inserted, updated }
}

/**
 * Upsert subjects and return inserted/updated counts.
 * Unique key: (school_id, subject_name)
 */
async function upsertSubjects(
  accessToken: string,
  schoolCode: string,
  subjects: ParsedSubject[]
): Promise<UpsertCounts> {
  if (subjects.length === 0) return { inserted: 0, updated: 0 }

  const existingResp = await fetch(
    `${SUPABASE_URL}/rest/v1/subjects?school_id=eq.${encodeURIComponent(schoolCode)}&select=subject_name`,
    {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    }
  )
  const existing: { subject_name: string }[] = existingResp.ok ? await existingResp.json() : []
  const existingNames = new Set(existing.map((s) => s.subject_name))

  const seenSubjects = new Set<string>()
  const rows = subjects
    .filter((s) => {
      if (seenSubjects.has(s.subject_name)) return false
      seenSubjects.add(s.subject_name)
      return true
    })
    .map((s) => ({
      school_id: schoolCode,
      subject_name: s.subject_name,
      subject_code: s.subject_code ?? null,
      color_code: s.color_code ?? null,
    }))

  const response = await fetch(`${SUPABASE_URL}/rest/v1/subjects?on_conflict=school_id,subject_name`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates,return=minimal',
    },
    body: JSON.stringify(rows),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(`[subjects] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
  }

  const inserted = rows.filter((r) => !existingNames.has(r.subject_name)).length
  const updated = rows.length - inserted
  return { inserted, updated }
}

/**
 * Upsert classes and return inserted/updated counts.
 * Unique key: (school_id, class_name)
 */
async function upsertClasses(
  accessToken: string,
  schoolCode: string,
  classes: ParsedClass[]
): Promise<UpsertCounts> {
  if (classes.length === 0) return { inserted: 0, updated: 0 }

  const existingResp = await fetch(
    `${SUPABASE_URL}/rest/v1/classes?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,class_name`,
    {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    }
  )
  const existing: { id: string; class_name: string }[] = existingResp.ok ? await existingResp.json() : []
  const existingNames = new Set(existing.map((c) => c.class_name))

  // Deduplicate by class_name for the classes table (unique key: school_id, class_name)
  const seen = new Set<string>()
  const uniqueClassRows = classes
    .filter((c) => { if (seen.has(c.class_name)) return false; seen.add(c.class_name); return true })
    .map((c) => ({ school_id: schoolCode, class_name: c.class_name, class_group: c.class_group }))

  const response = await fetch(`${SUPABASE_URL}/rest/v1/classes?on_conflict=school_id,class_name`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates,return=representation',
    },
    body: JSON.stringify(uniqueClassRows),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(`[classes] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
  }

  const upsertedClasses: { id: string; class_name: string }[] = await response.json()

  // Build class_name → id map from upserted + pre-existing
  const classIdMap = new Map<string, string>([
    ...existing.map((c): [string, string] => [c.class_name, c.id]),
    ...upsertedClasses.map((c): [string, string] => [c.class_name, c.id]),
  ])

  // Upsert class_sections — unique key: (class_id, section)
  // class_teacher_id is excluded from the batch entirely — the DB has UNIQUE(class_teacher_id)
  // which causes "ON CONFLICT DO UPDATE command cannot affect row a second time" when
  // multiple rows in the batch try to update the same unique slot.
  // Class teachers are set individually via PATCH after the bulk insert.
  const seenSections = new Set<string>()
  const sectionRows = classes
    .filter((c) => c.section && classIdMap.has(c.class_name))
    .filter((c) => {
      const key = `${classIdMap.get(c.class_name)}:${c.section}`
      if (seenSections.has(key)) return false
      seenSections.add(key)
      return true
    })
    .map((c) => ({
      class_id: classIdMap.get(c.class_name)!,
      section: c.section,
      // class_teacher_id intentionally omitted — set via individual PATCH below
    }))

  if (sectionRows.length > 0) {
    console.log('[class_sections] sending', sectionRows.length, 'rows, sample:', JSON.stringify(sectionRows.slice(0, 3)))
    // Use on_conflict with only the columns we want to update (not class_teacher_id)
    const sectResp = await fetch(`${SUPABASE_URL}/rest/v1/class_sections?on_conflict=class_id,section`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        // merge-duplicates will only update columns present in the payload
        // since class_teacher_id is absent, it won't be touched on conflict
        Prefer: 'resolution=merge-duplicates,return=minimal',
      },
      body: JSON.stringify(sectionRows),
    })
    if (!sectResp.ok) {
      const error = await sectResp.json()
      throw new Error(`[class_sections] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
    }
  }

  // Now set class teachers individually — one PATCH per section to avoid UNIQUE(class_teacher_id) conflicts
  const sectionsWithTeachers = classes.filter(
    (c) => c.class_teacher && c.section && classIdMap.has(c.class_name)
  )
  if (sectionsWithTeachers.length > 0) {
    // Fetch teacher id map for needed names
    const teacherNamesNeeded = [...new Set(sectionsWithTeachers.map((c) => c.class_teacher!))]
    const nameFilter = teacherNamesNeeded.map((n) => `name.eq.${encodeURIComponent(n)}`).join(',')
    const tResp = await fetch(
      `${SUPABASE_URL}/rest/v1/teachers?school_id=eq.${encodeURIComponent(schoolCode)}&or=(${nameFilter})&select=id,name`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
    )
    if (tResp.ok) {
      const teacherRows: { id: string; name: string }[] = await tResp.json()
      const teacherIdMap = new Map(teacherRows.map((t) => [t.name, t.id]))

      // Fetch section ids for the sections that need class teachers
      const classIds = [...new Set(sectionsWithTeachers.map((c) => classIdMap.get(c.class_name)!))]
      const sectionResp = await fetch(
        `${SUPABASE_URL}/rest/v1/class_sections?class_id=in.(${classIds.join(',')})&select=id,class_id,section`,
        { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
      )
      if (sectionResp.ok) {
        const fetchedSections: { id: string; class_id: string; section: string }[] = await sectionResp.json()
        const sectionIdMap = new Map(fetchedSections.map((s) => [`${s.class_id}:${s.section}`, s.id]))

        // PATCH each section individually — avoids the UNIQUE(class_teacher_id) batch conflict
        for (const c of sectionsWithTeachers) {
          const teacherId = teacherIdMap.get(c.class_teacher!)
          const classId = classIdMap.get(c.class_name)
          const sectionId = classId ? sectionIdMap.get(`${classId}:${c.section}`) : undefined
          if (!teacherId || !sectionId) continue

          await fetch(
            `${SUPABASE_URL}/rest/v1/class_sections?id=eq.${sectionId}`,
            {
              method: 'PATCH',
              headers: {
                apikey: SUPABASE_ANON_KEY,
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ class_teacher_id: teacherId }),
            }
          )
          // Ignore individual patch errors — a teacher may already be assigned elsewhere
        }
      }
    }
  }

  const inserted = uniqueClassRows.filter((c) => !existingNames.has(c.class_name)).length
  const updated = uniqueClassRows.length - inserted
  return { inserted, updated }
}

/**
 * Upsert rooms and return inserted/updated counts.
 * Unique key: (school_id, name)
 */
async function upsertRooms(
  accessToken: string,
  schoolCode: string,
  rooms: ParsedRoom[]
): Promise<UpsertCounts> {
  if (rooms.length === 0) return { inserted: 0, updated: 0 }

  const existingResp = await fetch(
    `${SUPABASE_URL}/rest/v1/rooms?school_id=eq.${encodeURIComponent(schoolCode)}&select=name`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const existing: { name: string }[] = existingResp.ok ? await existingResp.json() : []
  const existingNames = new Set(existing.map((r) => r.name))

  const seen = new Set<string>()
  const rows = rooms
    .filter((r) => {
      if (!r.name || seen.has(r.name)) return false
      seen.add(r.name)
      return true
    })
    .map((r) => ({
      school_id: schoolCode,
      name: r.name,
      description: r.description ?? null,
      capacity: r.capacity ?? null,
    }))

  if (rows.length === 0) return { inserted: 0, updated: 0 }

  const response = await fetch(`${SUPABASE_URL}/rest/v1/rooms?on_conflict=school_id,name`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates,return=minimal',
    },
    body: JSON.stringify(rows),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(`[rooms] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
  }

  const inserted = rows.filter((r) => !existingNames.has(r.name)).length
  const updated = rows.length - inserted
  return { inserted, updated }
}

/**
 * Normalize a time value to HH:MM:SS for Postgres TIME type.
 * Handles:
 *  - Excel decimal fractions (e.g. 0.354166... = 8:30 AM)
 *  - Excel date+time serials (e.g. 46096.333... — extract fractional part)
 *  - Decimal strings like "8.45" → "08:45:00"
 *  - Already-formatted strings like "8:45", "08:45:00"
 */
function normalizeTime(value: string | number): string {
  const num = typeof value === 'number' ? value : parseFloat(String(value))

  if (!isNaN(num)) {
    // Extract fractional (time) part — works for both pure fractions (< 1)
    // and date+time serials (e.g. 46096.3333 → 0.3333)
    const frac = num - Math.floor(num)

    if (frac > 0 || num === 0) {
      // It's a time serial — convert fraction of day to HH:MM:SS
      const totalMinutes = Math.round(frac * 24 * 60)
      const h = Math.floor(totalMinutes / 60)
      const m = totalMinutes % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
    }

    // Pure integer with no fractional part — treat as "8.45" style decimal
    // (e.g. user typed 845 meaning 8:45 — unlikely but handle gracefully)
    if (num >= 1) {
      const h = Math.floor(num)
      const m = Math.round((num - h) * 100)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
    }
  }

  // Already a string like "8:45" or "08:45:00"
  const str = String(value).trim()
  const parts = str.split(':')
  if (parts.length >= 2) {
    const h = parts[0].padStart(2, '0')
    const m = parts[1].padStart(2, '0')
    const s = parts[2] ? parts[2].padStart(2, '0') : '00'
    return `${h}:${m}:${s}`
  }

  return str // return as-is and let DB report the error
}

/**
 * Upsert schedule_periods scoped to a class_schedule_id and return inserted/updated counts.
 * Unique key: (class_schedule_id, period_number)
 */
async function upsertSchedulePeriods(
  accessToken: string,
  classScheduleId: string,
  periods: ParsedPeriod[]
): Promise<UpsertCounts> {
  if (periods.length === 0) return { inserted: 0, updated: 0 }

  const existingResp = await fetch(
    `${SUPABASE_URL}/rest/v1/schedule_periods?class_schedule_id=eq.${encodeURIComponent(classScheduleId)}&select=period_number`,
    {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    }
  )
  const existing: { period_number: number }[] = existingResp.ok
    ? await existingResp.json()
    : []
  const existingKeys = new Set(existing.map((p) => p.period_number))

  // Deduplicate by period_number — same period_number twice causes ON CONFLICT error
  const seenPeriods = new Set<number>()
  const rows = periods
    .filter((p) => {
      if (seenPeriods.has(p.period_number)) return false
      seenPeriods.add(p.period_number)
      return true
    })
    .map((p) => ({
      class_schedule_id: classScheduleId,
      period_number: p.period_number,
      time_start: normalizeTime(p.start_time),
      time_end: normalizeTime(p.end_time),
      is_break: p.is_break,
      // DB CHECK: break_type must be NULL when is_break=false, NOT NULL when is_break=true
      break_type: p.is_break ? (p.break_type ?? 'short') : null,
      period_name: p.period_name ?? null,
    }))

  console.log('[schedule_periods] sending', rows.length, 'rows')
  const response = await fetch(`${SUPABASE_URL}/rest/v1/schedule_periods?on_conflict=class_schedule_id,period_number`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates,return=minimal',
    },
    body: JSON.stringify(rows),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(`[schedule_periods] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
  }

  const inserted = rows.filter((r) => !existingKeys.has(r.period_number)).length
  const updated = rows.length - inserted
  return { inserted, updated }
}

/**
 * Upsert teacher-subject assignments and return inserted/updated counts.
 * Unique key: (teacher_id, subject_id)
 */
async function upsertTeacherSubjects(
  accessToken: string,
  schoolCode: string,
  teacherSubjects: ParsedTeacherSubject[]
): Promise<UpsertCounts> {
  if (teacherSubjects.length === 0) return { inserted: 0, updated: 0 }

  // Fetch teacher id map
  const teachersResp = await fetch(
    `${SUPABASE_URL}/rest/v1/teachers?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,name`,
    {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    }
  )
  const teacherRows: { id: string; name: string }[] = teachersResp.ok
    ? await teachersResp.json()
    : []
  const teacherMap = new Map(teacherRows.map((t) => [t.name, t.id]))

  // Fetch subject id map
  const subjectsResp = await fetch(
    `${SUPABASE_URL}/rest/v1/subjects?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,subject_name`,
    {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    }
  )
  const subjectRows: { id: string; subject_name: string }[] = subjectsResp.ok
    ? await subjectsResp.json()
    : []
  const subjectMap = new Map(subjectRows.map((s) => [s.subject_name, s.id]))

  // Fetch existing assignments scoped to this school's teachers
  const existingResp = await fetch(
    `${SUPABASE_URL}/rest/v1/teacher_subjects?select=teacher_id,subject_id&teacher_id=in.(${teacherRows.map(t => t.id).join(',') || 'null'})`,
    {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    }
  )
  const existingAssignments: { teacher_id: string; subject_id: string }[] = existingResp.ok
    ? await existingResp.json()
    : []
  const existingKeys = new Set(
    existingAssignments.map((a) => `${a.teacher_id}:${a.subject_id}`)
  )

  const rows = teacherSubjects
    .map((ts) => ({
      teacher_id: teacherMap.get(ts.teacher_name),
      subject_id: subjectMap.get(ts.subject_name),
    }))
    .filter((r): r is { teacher_id: string; subject_id: string } =>
      r.teacher_id != null && r.subject_id != null
    )

  // Deduplicate — same (teacher_id, subject_id) pair appearing twice in the
  // batch causes "ON CONFLICT DO UPDATE command cannot affect row a second time"
  const seen = new Set<string>()
  const uniqueRows = rows.filter((r) => {
    const key = `${r.teacher_id}:${r.subject_id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  if (uniqueRows.length === 0) return { inserted: 0, updated: 0 }

  console.log('[teacher_subjects] sending', uniqueRows.length, 'rows, sample:', JSON.stringify(uniqueRows.slice(0, 3)))

  const response = await fetch(`${SUPABASE_URL}/rest/v1/teacher_subjects?on_conflict=teacher_id,subject_id`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates,return=minimal',
    },
    body: JSON.stringify(uniqueRows),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(`[teacher_subjects] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
  }

  const inserted = uniqueRows.filter((r) => !existingKeys.has(`${r.teacher_id}:${r.subject_id}`)).length
  const updated = uniqueRows.length - inserted
  return { inserted, updated }
}

/**
 * Upsert teacher-class associations and return inserted/updated counts.
 * Unique key: (teacher_id, class_id, academic_year)
 * Uses the school's most recent academic year so it aligns with the UI display.
 */
async function upsertTeacherClasses(
  accessToken: string,
  schoolCode: string,
  teacherClasses: ParsedTeacherClass[],
  sessionAcademicYear: string
): Promise<UpsertCounts> {
  if (teacherClasses.length === 0) return { inserted: 0, updated: 0 }

  // Resolve the academic year to use — prefer the school's most recent year
  // (same as TeacherClassAssignmentTab uses), fall back to the session year
  let academicYear = sessionAcademicYear
  const ayResp = await fetch(
    `${SUPABASE_URL}/rest/v1/academic_years?school_id=eq.${encodeURIComponent(schoolCode)}&order=start_date.desc&limit=1&select=name`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  if (ayResp.ok) {
    const ayRows: { name: string }[] = await ayResp.json()
    if (ayRows[0]?.name) academicYear = ayRows[0].name
  }

  const teachersResp = await fetch(
    `${SUPABASE_URL}/rest/v1/teachers?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,name`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const teacherRows: { id: string; name: string }[] = teachersResp.ok ? await teachersResp.json() : []
  const teacherMap = new Map(teacherRows.map((t) => [t.name, t.id]))

  const classesResp = await fetch(
    `${SUPABASE_URL}/rest/v1/classes?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,class_name`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const classRows: { id: string; class_name: string }[] = classesResp.ok ? await classesResp.json() : []
  const classMap = new Map(classRows.map((c) => [c.class_name, c.id]))

  const rows = teacherClasses
    .map((tc) => ({
      teacher_id: teacherMap.get(tc.teacher_name),
      class_id: classMap.get(tc.class_name),
      academic_year: academicYear,
    }))
    .filter((r): r is { teacher_id: string; class_id: string; academic_year: string } =>
      r.teacher_id != null && r.class_id != null
    )

  const seen = new Set<string>()
  const uniqueRows = rows.filter((r) => {
    const key = `${r.teacher_id}:${r.class_id}:${r.academic_year}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  if (uniqueRows.length === 0) return { inserted: 0, updated: 0 }

  // Fetch existing to count inserts vs updates
  const teacherIds = [...new Set(uniqueRows.map((r) => r.teacher_id))].join(',')
  const existingResp = await fetch(
    `${SUPABASE_URL}/rest/v1/teacher_class_associations?teacher_id=in.(${teacherIds})&academic_year=eq.${encodeURIComponent(academicYear)}&select=teacher_id,class_id`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const existing: { teacher_id: string; class_id: string }[] = existingResp.ok ? await existingResp.json() : []
  const existingKeys = new Set(existing.map((e) => `${e.teacher_id}:${e.class_id}`))

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/teacher_class_associations?on_conflict=teacher_id,class_id,academic_year`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates,return=minimal',
      },
      body: JSON.stringify(uniqueRows),
    }
  )

  if (!response.ok) {
    const error = await response.json()
    throw new Error(`[teacher_class_associations] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
  }

  const inserted = uniqueRows.filter((r) => !existingKeys.has(`${r.teacher_id}:${r.class_id}`)).length
  const updated = uniqueRows.length - inserted
  return { inserted, updated }
}

/**
 * Upsert subject-class assignments and return inserted/updated counts.
 * Unique key: (school_id, class_id, subject_id)
 */
async function upsertSubjectClasses(
  accessToken: string,
  schoolCode: string,
  subjectClasses: ParsedSubjectClass[]
): Promise<UpsertCounts> {
  if (subjectClasses.length === 0) return { inserted: 0, updated: 0 }

  const subjectsResp = await fetch(
    `${SUPABASE_URL}/rest/v1/subjects?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,subject_name`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const subjectRows: { id: string; subject_name: string }[] = subjectsResp.ok ? await subjectsResp.json() : []
  const subjectMap = new Map(subjectRows.map((s) => [s.subject_name, s.id]))

  const classesResp = await fetch(
    `${SUPABASE_URL}/rest/v1/classes?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,class_name`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const classRows: { id: string; class_name: string }[] = classesResp.ok ? await classesResp.json() : []
  const classMap = new Map(classRows.map((c) => [c.class_name, c.id]))

  const rows = subjectClasses
    .map((sc) => ({
      school_id: schoolCode,
      subject_id: subjectMap.get(sc.subject_name),
      class_id: classMap.get(sc.class_name),
    }))
    .filter((r): r is { school_id: string; subject_id: string; class_id: string } =>
      r.subject_id != null && r.class_id != null
    )

  const seen = new Set<string>()
  const uniqueRows = rows.filter((r) => {
    const key = `${r.subject_id}:${r.class_id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  if (uniqueRows.length === 0) return { inserted: 0, updated: 0 }

  // Fetch existing to count inserts vs updates
  const classIds = [...new Set(uniqueRows.map((r) => r.class_id))].join(',')
  const existingResp = await fetch(
    `${SUPABASE_URL}/rest/v1/class_subjects?school_id=eq.${encodeURIComponent(schoolCode)}&class_id=in.(${classIds})&select=class_id,subject_id`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const existing: { class_id: string; subject_id: string }[] = existingResp.ok ? await existingResp.json() : []
  const existingKeys = new Set(existing.map((e) => `${e.subject_id}:${e.class_id}`))

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/class_subjects?on_conflict=school_id,class_id,subject_id`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates,return=minimal',
      },
      body: JSON.stringify(uniqueRows),
    }
  )

  if (!response.ok) {
    const error = await response.json()
    throw new Error(`[class_subjects] ${error.message || error.details || error.hint || JSON.stringify(error)}`)
  }

  const inserted = uniqueRows.filter((r) => !existingKeys.has(`${r.subject_id}:${r.class_id}`)).length
  const updated = uniqueRows.length - inserted
  return { inserted, updated }
}

/**
 * Upsert schedule assignments (class → class_schedule) by name.
 * Looks up class_schedule by name, then patches classes.class_schedule_id.
 */
async function upsertScheduleAssignments(
  accessToken: string,
  schoolCode: string,
  assignments: ParsedClassScheduleAssignment[]
): Promise<UpsertCounts> {
  if (assignments.length === 0) return { inserted: 0, updated: 0 }

  // Fetch all class_schedules for this school (name → id)
  const csResp = await fetch(
    `${SUPABASE_URL}/rest/v1/class_schedules?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,name`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const csRows: { id: string; name: string }[] = csResp.ok ? await csResp.json() : []
  const scheduleMap = new Map(csRows.map((s) => [s.name, s.id]))

  // Fetch all classes for this school (class_name → id)
  const classesResp = await fetch(
    `${SUPABASE_URL}/rest/v1/classes?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,class_name,class_schedule_id`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const classRows: { id: string; class_name: string; class_schedule_id: string | null }[] =
    classesResp.ok ? await classesResp.json() : []
  const classMap = new Map(classRows.map((c) => [c.class_name, c]))

  let inserted = 0
  let updated = 0

  for (const assignment of assignments) {
    const scheduleId = scheduleMap.get(assignment.schedule_name)
    const cls = classMap.get(assignment.class_name)
    if (!scheduleId || !cls) continue

    const wasAssigned = cls.class_schedule_id === scheduleId
    const patchResp = await fetch(
      `${SUPABASE_URL}/rest/v1/classes?id=eq.${cls.id}`,
      {
        method: 'PATCH',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ class_schedule_id: scheduleId }),
      }
    )
    if (patchResp.ok) {
      if (wasAssigned) updated++; else inserted++
    }
  }

  return { inserted, updated }
}

/**
 * Upsert timetable records and return inserted/updated counts.
 * Inserts new records; existing records for the same school/academic_year are superseded.
 */
async function upsertTimetableRecords(
  accessToken: string,
  schoolCode: string,
  timetableRows: TimetableRow[],
  academicYear: string,
  validFrom: string,
  validTo: string | null,
  createdBy: string
): Promise<UpsertCounts> {
  if (timetableRows.length === 0) return { inserted: 0, updated: 0 }

  // Fetch lookup maps — periods need a two-step lookup via class_schedules
  const [teachersResp, subjectsResp, classesResp, schedulesResp] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/teachers?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,name`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/subjects?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,subject_name`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/classes?school_id=eq.${encodeURIComponent(schoolCode)}&select=id,class_name`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/class_schedules?school_id=eq.${encodeURIComponent(schoolCode)}&select=id`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
    ),
  ])

  const teacherRows: { id: string; name: string }[] = teachersResp.ok ? await teachersResp.json() : []
  const subjectRows: { id: string; subject_name: string }[] = subjectsResp.ok ? await subjectsResp.json() : []
  const classRows: { id: string; class_name: string }[] = classesResp.ok ? await classesResp.json() : []
  const scheduleIds: { id: string }[] = schedulesResp.ok ? await schedulesResp.json() : []

  // Fetch schedule_periods scoped to this school's class_schedules
  let periodRows: { id: string; period_number: number }[] = []
  if (scheduleIds.length > 0) {
    const ids = scheduleIds.map(s => s.id).join(',')
    const periodsResp = await fetch(
      `${SUPABASE_URL}/rest/v1/schedule_periods?class_schedule_id=in.(${ids})&select=id,period_number`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
    )
    if (periodsResp.ok) periodRows = await periodsResp.json()
  }

  const teacherMap = new Map(teacherRows.map((t) => [t.name, t.id]))
  const subjectMap = new Map(subjectRows.map((s) => [s.subject_name, s.id]))
  const classMap = new Map(classRows.map((c) => [c.class_name, c.id]))
  // period lookup by period_number only (schedule_periods are school-scoped via class_schedule)
  const periodMap = new Map(periodRows.map((p) => [p.period_number, p.id]))

  // Fetch class_sections scoped to this school
  const classSectionsResp = await fetch(
    `${SUPABASE_URL}/rest/v1/class_sections?select=id,class_id,section&class_id=in.(${classRows.map(c => c.id).join(',') || 'null'})`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  )
  const classSectionRows: { id: string; class_id: string; section: string }[] =
    classSectionsResp.ok ? await classSectionsResp.json() : []
  const classSectionMap = new Map(
    classSectionRows.map((cs) => [`${cs.class_id}:${cs.section}`, cs.id])
  )

  const rows = timetableRows
    .map((row) => {
      const teacherId = teacherMap.get(row.teacher)
      const subjectId = subjectMap.get(row.subject)
      const classId = classMap.get(row.class)
      const periodId = periodMap.get(row.period)
      const classSectionId = classId
        ? classSectionMap.get(`${classId}:${row.section}`)
        : undefined

      if (!teacherId || !subjectId || !classSectionId || !periodId) return null

      return {
        school_id: schoolCode,
        teacher_id: teacherId,
        subject_id: subjectId,
        class_section_id: classSectionId,
        period_id: periodId,
        academic_year: academicYear,
        valid_from: validFrom,
        valid_to: validTo ?? null,
        day_of_week: row.day.toUpperCase(),
        created_by: createdBy,
      }
    })
    .filter(Boolean)

  if (rows.length === 0) return { inserted: 0, updated: 0 }

  // Close any existing records for this school+academic_year that overlap with the new version.
  // We set their valid_to = validFrom - 1 day, covering both open-ended (NULL) and
  // previously-set end dates that fall on or after the new effective date.
  const closeDate = new Date(validFrom)
  closeDate.setDate(closeDate.getDate() - 1)
  const closeDateStr = closeDate.toISOString().slice(0, 10)

  // Close records where valid_to IS NULL (open-ended)
  const closeNullResp = await fetch(
    `${SUPABASE_URL}/rest/v1/timetable_records?school_id=eq.${encodeURIComponent(schoolCode)}&academic_year=eq.${encodeURIComponent(academicYear)}&valid_to=is.null`,
    {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ valid_to: closeDateStr }),
    }
  )
  if (!closeNullResp.ok) {
    const error = await closeNullResp.json()
    throw new Error(error.message || 'Failed to close existing timetable records')
  }

  // Also close records where valid_to >= validFrom (overlap with new version)
  const closeOverlapResp = await fetch(
    `${SUPABASE_URL}/rest/v1/timetable_records?school_id=eq.${encodeURIComponent(schoolCode)}&academic_year=eq.${encodeURIComponent(academicYear)}&valid_to=gte.${validFrom}`,
    {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ valid_to: closeDateStr }),
    }
  )
  if (!closeOverlapResp.ok) {
    const error = await closeOverlapResp.json()
    throw new Error(error.message || 'Failed to close overlapping timetable records')
  }

  // Insert new records
  const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Prefer: 'return=minimal',
    },
    body: JSON.stringify(rows),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to insert timetable records')
  }

  return { inserted: rows.length, updated: 0 }
}

/**
 * Execute the full import for a session.
 * Upserts in dependency order: teachers → subjects → classes → periods →
 * teacher-subjects → timetable records (if include_timetable).
 * Returns per-entity inserted/updated counts.
 */
export async function executeImportDirect(
  sessionId: string,
  userId: string
): Promise<ImportResult> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolCode = getSchoolContext()
    if (!schoolCode) throw new Error('No school context')

    // Fetch the session
    const sessionResp = await fetch(
      `${SUPABASE_URL}/rest/v1/import_sessions?id=eq.${sessionId}&select=*`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/vnd.pgrst.object+json',
        },
      }
    )
    if (!sessionResp.ok) throw new Error('Failed to fetch import session')
    const session: ImportSession = await sessionResp.json()

    if (!session.master_data_json) throw new Error('No master data found in session')

    const masterData: ParsedMasterData = session.master_data_json

    console.log('[import] starting upserts — teachers:', masterData.teachers.length, 'subjects:', masterData.subjects.length, 'classes:', masterData.classes.length, 'periods:', masterData.schedules.flatMap(s=>s.periods).length, 'teacherSubjects:', masterData.teacherSubjects.length)

    // Upsert in dependency order
    const teacherCounts = await upsertTeachers(accessToken, schoolCode, masterData.teachers)
    console.log('[import] teachers done:', teacherCounts)
    const subjectCounts = await upsertSubjects(accessToken, schoolCode, masterData.subjects)
    console.log('[import] subjects done:', subjectCounts)
    const classCounts = await upsertClasses(accessToken, schoolCode, masterData.classes)
    console.log('[import] classes done:', classCounts)

    const roomCounts = await upsertRooms(accessToken, schoolCode, masterData.rooms ?? [])
    console.log('[import] rooms done:', roomCounts)

    // Upsert named class schedules + their periods
    let periodCounts: UpsertCounts = { inserted: 0, updated: 0 }
    if (masterData.schedules && masterData.schedules.length > 0) {
      for (const schedule of masterData.schedules) {
        // Upsert the class_schedule by name (unique key: school_id, name)
        const csUpsertResp = await fetch(
          `${SUPABASE_URL}/rest/v1/class_schedules?on_conflict=school_id,name`,
          {
            method: 'POST',
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
              Prefer: 'resolution=merge-duplicates,return=representation',
            },
            body: JSON.stringify({
              school_id: schoolCode,
              name: schedule.name,
              description: schedule.description ?? null,
              working_days: schedule.working_days ?? ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
              periods_count: schedule.periods.filter((p) => !p.is_break).length,
            }),
          }
        )
        if (!csUpsertResp.ok) {
          const err = await csUpsertResp.json()
          throw new Error(`[class_schedules] ${err.message || err.details || JSON.stringify(err)}`)
        }
        const csRows: { id: string }[] = await csUpsertResp.json()
        const classScheduleId = csRows[0]?.id
        if (!classScheduleId) throw new Error(`Could not resolve id for schedule "${schedule.name}"`)

        const counts = await upsertSchedulePeriods(accessToken, classScheduleId, schedule.periods)
        periodCounts = { inserted: periodCounts.inserted + counts.inserted, updated: periodCounts.updated + counts.updated }
      }
    }
    console.log('[import] schedules+periods done:', periodCounts)

    // Upsert schedule assignments (class → schedule)
    const scheduleAssignmentCounts = await upsertScheduleAssignments(
      accessToken,
      schoolCode,
      masterData.scheduleAssignments ?? []
    )
    console.log('[import] scheduleAssignments done:', scheduleAssignmentCounts)
    const teacherSubjectCounts = await upsertTeacherSubjects(
      accessToken,
      schoolCode,
      masterData.teacherSubjects
    )

    const teacherClassCounts = await upsertTeacherClasses(
      accessToken,
      schoolCode,
      masterData.teacherClasses ?? [],
      session.academic_year
    )
    console.log('[import] teacherClasses done:', teacherClassCounts)

    const subjectClassCounts = await upsertSubjectClasses(
      accessToken,
      schoolCode,
      masterData.subjectClasses ?? []
    )
    console.log('[import] subjectClasses done:', subjectClassCounts)

    let timetableCounts: { inserted: number; updated: number } | null = null
    if (session.include_timetable && session.timetable_json) {
      timetableCounts = await upsertTimetableRecords(
        accessToken,
        schoolCode,
        session.timetable_json,
        session.academic_year,
        session.date_range_from ?? new Date().toISOString().slice(0, 10),
        session.date_range_to,
        userId
      )
    }

    // Mark session as completed
    await fetch(`${SUPABASE_URL}/rest/v1/import_sessions?id=eq.${sessionId}`, {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        status: 'completed',
        locked_by: null,
        locked_at: null,
        updated_by: userId,
      }),
    })

    return {
      teachers: teacherCounts,
      subjects: subjectCounts,
      classes: classCounts,
      rooms: roomCounts,
      schedules: periodCounts,
      scheduleAssignments: scheduleAssignmentCounts,
      teacherSubjects: teacherSubjectCounts,
      teacherClasses: teacherClassCounts,
      subjectClasses: subjectClassCounts,
      timetableRecords: timetableCounts,
    }
  })
}

// ============================================================================
// Export Data — Requirements 13.1, 13.2
// ============================================================================

/**
 * Fetch all master data and timetable records for a school/academic year
 * and return a structured ExportData object.
 */
export async function generateExportDataDirect(
  schoolCode: string,
  academicYear: string
): Promise<ExportData> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` }
    const sc = encodeURIComponent(schoolCode)
    const ay = encodeURIComponent(academicYear)

    // Fetch all entities in parallel — schedule_periods needs class_schedule IDs first
    const [teachersResp, subjectsResp, classesResp, schedulesResp, tsResp, tcResp, scResp, roomsResp] =
      await Promise.all([
        fetch(
          `${SUPABASE_URL}/rest/v1/teachers?school_id=eq.${sc}&select=name,email,phone_number,teacher_type:teacher_types(type_name)&order=name.asc`,
          { headers }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/subjects?school_id=eq.${sc}&select=subject_name,subject_code,color_code&order=subject_name.asc`,
          { headers }
        ),
        // Fetch classes with sections, class teacher, AND assigned schedule name
        fetch(
          `${SUPABASE_URL}/rest/v1/classes?school_id=eq.${sc}&select=class_name,class_group,class_schedule_id,class_schedule:class_schedules(name),class_sections(section,class_teacher:teachers(name))&order=class_name.asc`,
          { headers }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/class_schedules?school_id=eq.${sc}&select=id,name,description,working_days&order=created_at.asc`,
          { headers }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/teacher_subjects?select=teacher:teachers!inner(name,school_id),subject:subjects!inner(subject_name,school_id)&teacher.school_id=eq.${sc}&subject.school_id=eq.${sc}`,
          { headers }
        ),
        // Teacher-class associations for this school's academic year
        fetch(
          `${SUPABASE_URL}/rest/v1/teacher_class_associations?select=teacher:teachers!inner(name),class:classes!inner(class_name)&teacher.school_id=eq.${sc}&academic_year=eq.${ay}`,
          { headers }
        ),
        // Subject-class assignments for this school
        fetch(
          `${SUPABASE_URL}/rest/v1/class_subjects?school_id=eq.${sc}&select=subject:subjects(subject_name),class:classes(class_name)`,
          { headers }
        ),
        // Rooms
        fetch(
          `${SUPABASE_URL}/rest/v1/rooms?school_id=eq.${sc}&select=name,description,capacity&order=name.asc`,
          { headers }
        ),
      ])

    // Fetch schedule_periods with their schedule name
    let periodsResp: Response | null = null
    if (schedulesResp.ok) {
      const scheduleRows: { id: string; name: string; description: string | null; working_days: string[] }[] = await schedulesResp.json()
      if (scheduleRows.length > 0) {
        const ids = scheduleRows.map((s) => s.id).join(',')
        periodsResp = await fetch(
          `${SUPABASE_URL}/rest/v1/schedule_periods?class_schedule_id=in.(${ids})&select=class_schedule_id,period_number,time_start,time_end,is_break,period_name,break_type&order=class_schedule_id.asc,period_number.asc`,
          { headers }
        )
        // Store schedule rows for later use
        ;(schedulesResp as any)._parsed = scheduleRows
      }
    }

    // Paginate timetable records — Supabase caps at 1000 rows per request
    const ttBase = `${SUPABASE_URL}/rest/v1/timetable_records?school_id=eq.${sc}&academic_year=eq.${ay}&is_supervision=is.false&select=day_of_week,period:schedule_periods(period_number),teacher:teachers(name),subject:subjects(subject_name),class_section:class_sections(section,class:classes(class_name))&order=day_of_week.asc`
    console.log('[Export] timetable query URL:', ttBase)
    const rawTt: any[] = []
    const PAGE = 1000
    let offset = 0
    while (true) {
      const resp = await fetch(`${ttBase}&limit=${PAGE}&offset=${offset}`, {
        headers: { ...headers, Prefer: 'count=none' },
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`Failed to fetch timetable records: ${resp.status} ${errText}`)
      }
      const page: any[] = await resp.json()
      rawTt.push(...page)
      if (page.length < PAGE) break
      offset += PAGE
    }

    if (!teachersResp.ok) throw new Error('Failed to fetch teachers for export')
    if (!subjectsResp.ok) throw new Error('Failed to fetch subjects for export')
    if (!classesResp.ok) throw new Error('Failed to fetch classes for export')

    const rawTeachers: any[] = await teachersResp.json()
    const rawSubjects: any[] = await subjectsResp.json()
    const rawClasses: any[] = await classesResp.json()
    const rawScheduleRows: { id: string; name: string; description: string | null; working_days: string[] }[] =
      (schedulesResp as any)._parsed ?? []
    const rawPeriods: any[] = periodsResp?.ok ? await periodsResp.json() : []
    const rawTs: any[] = tsResp.ok ? await tsResp.json() : []
    const rawTc: any[] = tcResp.ok ? await tcResp.json() : []
    const rawSc: any[] = scResp.ok ? await scResp.json() : []
    const rawRooms: any[] = roomsResp.ok ? await roomsResp.json() : []

    console.log('[Export] teachers:', rawTeachers.length, 'subjects:', rawSubjects.length, 'classes:', rawClasses.length, 'schedules:', rawScheduleRows.length, 'periods:', rawPeriods.length, 'rooms:', rawRooms.length, 'timetableRows raw:', rawTt.length)
    if (rawTt.length > 0) console.log('[Export] sample timetable row:', JSON.stringify(rawTt[0]))

    const teachers: ParsedTeacher[] = rawTeachers.map((t) => ({
      name: t.name,
      email: t.email ?? undefined,
      phone: t.phone_number ?? undefined,
      teacher_type: t.teacher_type?.type_name ?? '',
    }))

    const subjects: ParsedSubject[] = rawSubjects.map((s) => ({
      subject_name: s.subject_name,
      subject_code: s.subject_code ?? undefined,
      color_code: s.color_code ?? undefined,
    }))

    // Flatten classes → one row per section (same format as import)
    const classes: ParsedClass[] = rawClasses.flatMap((c: any) =>
      (c.class_sections ?? []).map((cs: any) => ({
        class_name: c.class_name,
        class_group: c.class_group ?? '',
        section: cs.section ?? '',
        class_teacher: cs.class_teacher?.name ?? undefined,
      }))
    )

    // Build named schedules with their periods grouped by class_schedule_id
    const periodsByScheduleId = new Map<string, any[]>()
    rawPeriods.forEach((p: any) => {
      const sid = p.class_schedule_id
      if (!periodsByScheduleId.has(sid)) periodsByScheduleId.set(sid, [])
      periodsByScheduleId.get(sid)!.push(p)
    })

    const schedules = rawScheduleRows.map((s) => ({
      name: s.name,
      description: s.description ?? undefined,
      working_days: s.working_days,
      periods: (periodsByScheduleId.get(s.id) ?? []).map((p: any) => ({
        period_number: p.period_number,
        start_time: p.time_start,
        end_time: p.time_end,
        is_break: p.is_break,
        period_name: p.period_name ?? undefined,
        break_type: p.break_type ?? undefined,
      })),
    }))

    // Build schedule assignments from classes that have a class_schedule_id
    const scheduleIdToName = new Map(rawScheduleRows.map((s) => [s.id, s.name]))
    const scheduleAssignments = rawClasses
      .filter((c: any) => c.class_schedule_id && scheduleIdToName.has(c.class_schedule_id))
      .map((c: any) => ({
        class_name: c.class_name,
        schedule_name: scheduleIdToName.get(c.class_schedule_id)!,
      }))

    // Filter teacher_subjects to only those belonging to this school
    const teacherSubjects: ParsedTeacherSubject[] = rawTs
      .filter((ts) => ts.teacher?.name && ts.subject?.subject_name &&
        ts.teacher?.school_id === schoolCode && ts.subject?.school_id === schoolCode)
      .map((ts) => ({
        teacher_name: ts.teacher.name,
        subject_name: ts.subject.subject_name,
      }))

    const teacherClasses: ParsedTeacherClass[] = rawTc
      .filter((tc) => tc.teacher?.name && tc.class?.class_name)
      .map((tc) => ({
        teacher_name: tc.teacher.name,
        class_name: tc.class.class_name,
      }))

    const subjectClasses: ParsedSubjectClass[] = rawSc
      .filter((sc) => sc.subject?.subject_name && sc.class?.class_name)
      .map((sc) => ({
        subject_name: sc.subject.subject_name,
        class_name: sc.class.class_name,
      }))

    const rooms: ParsedRoom[] = rawRooms.map((r: any) => ({
      name: r.name,
      description: r.description ?? undefined,
      capacity: r.capacity ?? undefined,
    }))

    const timetableRows: TimetableRow[] = rawTt
      .filter(
        (r) =>
          r.teacher?.name &&
          r.subject?.subject_name &&
          r.class_section?.class?.class_name
      )
      .map((r) => ({
        class: r.class_section.class.class_name,
        section: r.class_section.section,
        // Normalize to title case to match VALID_DAYS in the timetable template
        day: r.day_of_week.charAt(0) + r.day_of_week.slice(1).toLowerCase(),
        period: r.period?.period_number ?? 0,
        teacher: r.teacher.name,
        subject: r.subject.subject_name,
      }))

    return {
      academicYear,
      teachers,
      subjects,
      classes,
      rooms,
      schedules,
      scheduleAssignments,
      teacherSubjects,
      teacherClasses,
      subjectClasses,
      timetableRows,
    }
  })
}

/**
 * Fetch existing master data for a school as ParsedMasterData.
 * Used to pre-fill the template download with current DB data.
 */
export async function fetchExistingMasterDataDirect(schoolCode: string): Promise<ParsedMasterData> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` }
    const sc = encodeURIComponent(schoolCode)

    const [teachersResp, subjectsResp, classesResp, schedulesResp, tsResp, tcResp, scResp, roomsResp] = await Promise.all([
      // Teachers with their type name
      fetch(
        `${SUPABASE_URL}/rest/v1/teachers?school_id=eq.${sc}&select=name,email,phone_number,teacher_type:teacher_types(type_name)&order=name.asc`,
        { headers }
      ),
      // Subjects with all fields including color_code
      fetch(
        `${SUPABASE_URL}/rest/v1/subjects?school_id=eq.${sc}&select=subject_name,subject_code,color_code&order=subject_name.asc`,
        { headers }
      ),
      // Classes with their sections, class teacher, and assigned schedule
      fetch(
        `${SUPABASE_URL}/rest/v1/classes?school_id=eq.${sc}&select=class_name,class_group,class_schedule_id,class_sections(section,class_teacher:teachers(name))&order=class_name.asc`,
        { headers }
      ),
      // Fetch class_schedule IDs for this school first
      fetch(
        `${SUPABASE_URL}/rest/v1/class_schedules?school_id=eq.${sc}&select=id,name,description,working_days&order=created_at.asc`,
        { headers }
      ),
      // Teacher-subjects filtered to this school via inner join on teacher
      fetch(
        `${SUPABASE_URL}/rest/v1/teacher_subjects?select=teacher:teachers!inner(name,school_id),subject:subjects!inner(subject_name,school_id)&teacher.school_id=eq.${sc}&subject.school_id=eq.${sc}`,
        { headers }
      ),
      // Teacher-class associations (most recent academic year) — filter via teacher IDs
      fetch(
        `${SUPABASE_URL}/rest/v1/teacher_class_associations?select=academic_year,teacher:teachers!inner(name),class:classes!inner(class_name)&teacher.school_id=eq.${sc}&order=academic_year.desc`,
        { headers }
      ),
      // Subject-class assignments — already scoped by school_id
      fetch(
        `${SUPABASE_URL}/rest/v1/class_subjects?school_id=eq.${sc}&select=subject:subjects(subject_name),class:classes(class_name)`,
        { headers }
      ),
      // Rooms
      fetch(
        `${SUPABASE_URL}/rest/v1/rooms?school_id=eq.${sc}&select=name,description,capacity&order=name.asc`,
        { headers }
      ),
    ])

    if (!teachersResp.ok) {
      const e = await teachersResp.json().catch(() => ({}))
      throw new Error(`Failed to fetch teachers: ${e?.message ?? teachersResp.statusText}`)
    }
    if (!subjectsResp.ok) {
      const e = await subjectsResp.json().catch(() => ({}))
      throw new Error(`Failed to fetch subjects: ${e?.message ?? subjectsResp.statusText}`)
    }

    // Fetch schedule_periods with schedule metadata
    let periodsResp: Response | null = null
    let rawScheduleRows: { id: string; name: string; description: string | null; working_days: string[] }[] = []
    if (schedulesResp.ok) {
      rawScheduleRows = await schedulesResp.json()
      if (rawScheduleRows.length > 0) {
        const ids = rawScheduleRows.map((s) => s.id).join(',')
        periodsResp = await fetch(
          `${SUPABASE_URL}/rest/v1/schedule_periods?class_schedule_id=in.(${ids})&select=class_schedule_id,period_number,time_start,time_end,is_break,period_name,break_type&order=class_schedule_id.asc,period_number.asc`,
          { headers }
        )
      }
    }

    if (periodsResp && !periodsResp.ok) {
      const e = await periodsResp.json().catch(() => ({}))
      throw new Error(`Failed to fetch periods: ${e?.message ?? periodsResp.statusText}`)
    }

    const rawTeachers: any[] = await teachersResp.json()
    const rawSubjects: any[] = await subjectsResp.json()
    const rawClasses: any[] = classesResp.ok ? await classesResp.json() : []
    const rawPeriods: any[] = periodsResp?.ok ? await periodsResp.json() : []
    const rawTs: any[] = tsResp.ok ? await tsResp.json() : []
    const rawTc: any[] = tcResp.ok ? await tcResp.json() : []
    const rawSc: any[] = scResp.ok ? await scResp.json() : []
    const rawRooms: any[] = roomsResp.ok ? await roomsResp.json() : []

    const teachers: ParsedTeacher[] = rawTeachers.map((t) => ({
      name: t.name,
      email: t.email ?? undefined,
      phone: t.phone_number ?? undefined,
      teacher_type: t.teacher_type?.type_name ?? '',
    }))

    const subjects: ParsedSubject[] = rawSubjects.map((s) => ({
      subject_name: s.subject_name,
      subject_code: s.subject_code ?? undefined,
      color_code: s.color_code ?? undefined,
    }))

    // Flatten classes → sections (one ParsedClass row per section)
    const classes: ParsedClass[] = rawClasses.flatMap((c: any) =>
      (c.class_sections ?? []).map((cs: any) => ({
        class_name: c.class_name,
        class_group: c.class_group ?? '',
        section: cs.section ?? '',
        class_teacher: cs.class_teacher?.name ?? undefined,
      }))
    )

    // Build named schedules with periods grouped by schedule
    const periodsByScheduleId = new Map<string, any[]>()
    rawPeriods.forEach((p: any) => {
      const sid = p.class_schedule_id
      if (!periodsByScheduleId.has(sid)) periodsByScheduleId.set(sid, [])
      periodsByScheduleId.get(sid)!.push(p)
    })

    const schedules = rawScheduleRows.map((s) => ({
      name: s.name,
      description: s.description ?? undefined,
      working_days: s.working_days,
      periods: (periodsByScheduleId.get(s.id) ?? []).map((p: any) => ({
        period_number: p.period_number,
        start_time: p.time_start,
        end_time: p.time_end,
        is_break: p.is_break,
        period_name: p.period_name ?? undefined,
        break_type: p.break_type ?? undefined,
      })),
    }))

    // Build schedule assignments from classes that have a class_schedule_id
    const scheduleIdToName = new Map(rawScheduleRows.map((s) => [s.id, s.name]))
    const scheduleAssignments = rawClasses
      .filter((c: any) => c.class_schedule_id && scheduleIdToName.has(c.class_schedule_id))
      .map((c: any) => ({
        class_name: c.class_name,
        schedule_name: scheduleIdToName.get(c.class_schedule_id)!,
      }))

    // Filter to this school — scoped via !inner join on teacher.school_id
    const teacherSubjects: ParsedTeacherSubject[] = rawTs
      .filter((ts) => ts.teacher?.name && ts.subject?.subject_name)
      .map((ts) => ({
        teacher_name: ts.teacher.name,
        subject_name: ts.subject.subject_name,
      }))

    const teacherClasses: ParsedTeacherClass[] = rawTc
      .filter((tc) => tc.teacher?.name && tc.class?.class_name)
      .map((tc) => ({
        teacher_name: tc.teacher.name,
        class_name: tc.class.class_name,
      }))

    // Deduplicate teacher-class pairs (multiple academic years may produce duplicates)
    const seenTc = new Set<string>()
    const uniqueTeacherClasses = teacherClasses.filter((tc) => {
      const key = `${tc.teacher_name}:${tc.class_name}`
      if (seenTc.has(key)) return false
      seenTc.add(key)
      return true
    })

    const subjectClasses: ParsedSubjectClass[] = rawSc
      .filter((sc) => sc.subject?.subject_name && sc.class?.class_name)
      .map((sc) => ({
        subject_name: sc.subject.subject_name,
        class_name: sc.class.class_name,
      }))

    const rooms: ParsedRoom[] = rawRooms.map((r: any) => ({
      name: r.name,
      description: r.description ?? undefined,
      capacity: r.capacity ?? undefined,
    }))

    return { teachers, subjects, classes, rooms, schedules, scheduleAssignments, teacherSubjects, teacherClasses: uniqueTeacherClasses, subjectClasses }
  })
}
